import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle
} from "react";
import styled from "styled-components";
import { Box, Text, constants, util, hooks } from "@thecb/components";

const { useTurnstileScript } = hooks;
const { noop } = util.general;
const { ERROR_COLOR } = constants.colors;

const TurnstileWidgetContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  padding: ${({ padding }) => padding};
  justify-content: ${({ justify }) => justify};
  align-items: ${({ align }) => align};
  width: 100%;
`;

const FATAL_ERROR_CODES = [
  // Configuration errors
  /^100/,
  /^105/,
  /^110100$/,
  /^110110$/,
  /^110200$/,
  /^110420$/,
  /^110430$/,
  /^400020$/,
  /^400030$/,
  /^400040$/,
  // Browser/environment errors
  /^110500$/,
  /^110510$/,
  /^200010$/,
  /^200100$/,
  // Internal errors
  /^120/
];

const TurnstileWidget = forwardRef(
  (
    {
      verifyURL, // URL to load the Turnstile script from
      siteKey, // Turnstile site key
      onSuccess = noop,
      onError = noop,
      onExpired = noop,
      padding = "1rem",
      justify = "flex-end",
      align = "flex-end",
      size = "normal",
      tabindex = 0,
      retry = "never",
      theme = "light",
      extraStyles = ""
    },
    ref
  ) => {
    if (!verifyURL || !siteKey) return null;

    const widgetRef = useRef(null);
    const widgetIdRef = useRef(null);
    const [error, setError] = useState(null);

    const { scriptLoaded, scriptError } = useTurnstileScript(verifyURL);

    // Clear the error state
    const clearError = () => {
      setError(null);
    };

    // Handle errors based on the retry strategy
    const handleError = errorCode => {
      if (!errorCode) return;
      const isFatalError = FATAL_ERROR_CODES.some(pattern =>
        pattern.test(errorCode)
      );
      const errorMessage = getErrorMessage(isFatalError);
      setError(errorMessage);
    };

    const getErrorMessage = isFatalError => {
      if (isFatalError) {
        return "Browser or system error. Please refresh the page or try a different browser.";
      }
      return "Something went wrong. Please refresh and try again.";
    };

    // Allow the parent component to reset the Turnstile widget
    useImperativeHandle(
      ref,
      () => ({
        reset: () => {
          if (widgetIdRef.current && window.turnstile) {
            window.turnstile.reset(widgetIdRef.current);
            clearError();
          }
        }
      }),
      []
    );

    useEffect(() => {
      if (scriptError) {
        setError(
          "Unable to load security verification. Please refresh the page."
        );
        onError?.("script_load_failed");
        return;
      }

      if (widgetIdRef.current || !window.turnstile || !scriptLoaded) return;

      try {
        widgetIdRef.current = window.turnstile.render(widgetRef.current, {
          sitekey: siteKey,
          size: size,
          retry: retry,
          tabindex: tabindex,
          theme: theme,
          callback: token => {
            clearError();
            onSuccess?.(token);
          },
          "error-callback": errorCode => {
            handleError(errorCode);
            onError?.(errorCode);
          },
          "expired-callback": () => {
            clearError();
            onExpired?.();
          }
        });
      } catch (error) {
        setError(
          "Unable to load security verification. Please refresh the page."
        );
        onError?.("render_failed");
      }

      return () => {
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.remove(widgetIdRef.current);
          widgetIdRef.current = null;
          clearError();
        }
      };
    }, [scriptLoaded, scriptError, siteKey]);

    return (
      <>
        <TurnstileWidgetContainer
          padding={padding}
          justify={justify}
          align={align}
          extraStyles={extraStyles}
        >
          <div ref={widgetRef} />
          {error && (
            <Text
              color={ERROR_COLOR}
              variant="pXS"
              extraStyles={`
                word-break: break-word;
                text-align: end;
                padding-top: 0.5rem;
              `}
            >
              {error}
            </Text>
          )}
        </TurnstileWidgetContainer>
      </>
    );
  }
);

export default TurnstileWidget;
